<template>
    <div>
        <p class="typographies-paragraph-medium-regular">
            {{ $l10n.__('Consulter l’ensemble des paramètres renseignés lors de votre import') }}
        </p>
        <div class="list-settings-used">
            <!--    Champ de comparaison (identifiers)        -->
            <template v-if="!isImportV2">
                <p
                    v-for="(identifierOptions, identifier) in importOptions.identifiers"
                    :key="identifier"
                    class="line-setting"
                >
                    <span class="title typographies-paragraph-medium-semibold">
                        {{ identifierOptions.title }}
                    </span>
                    <span class="typographies-paragraph-medium-regular">
                        {{
                            getLabelIdentifier(
                                identifier.toString(),
                                importDetail._embed.metadata.identifiers[identifier],
                            )
                        }}
                    </span>
                </p>
            </template>
            <!--    Si la donnée n’existe pas dans Sellsy        -->
            <p v-if="shouldDisplayCreateUpdateFieldsOptions" class="line-setting">
                <span class="title typographies-paragraph-medium-semibold">
                    {{ getTitleSetting('create_and_update') }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    {{ getCreateOrUpdateSettingLabel() }}
                </span>
            </p>
            <!--    Si une cellule est vide        -->
            <p class="line-setting">
                <span class="title typographies-paragraph-medium-semibold">
                    {{ getTitleSetting('update_empty_fields') }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    {{
                        getLabelSetting(
                            'update_empty_fields',
                            importDetail._embed.metadata.update_empty_fields,
                        )
                    }}
                </span>
            </p>
            <!--    Smart-tags        -->
            <p v-if="shouldDisplayCreateUpdateFieldsOptions" class="line-setting">
                <span class="title typographies-paragraph-medium-semibold">
                    {{ getTitleSetting('overwrite_tags') }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    {{
                        getLabelSetting(
                            'overwrite_tags',
                            importDetail._embed.metadata.overwrite_tags,
                        )
                    }}
                </span>
            </p>
            <!--    Si un champ personnalisé obligatoire de mon fichier est vide        -->
            <p v-if="shouldDisplayCustomFieldsOptions" class="line-setting">
                <span class="title typographies-paragraph-medium-semibold">
                    {{ $l10n.__('Si un champ personnalisé obligatoire de mon fichier est vide') }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    {{
                        getLabelSetting(
                            'dismiss_required_custom_fields',
                            importDetail._embed.metadata.dismiss_required_custom_fields,
                        )
                    }}
                </span>
            </p>
            <!--    Si une cellule de champ personnalisé de mon fichier est vide        -->
            <p v-if="shouldDisplayCustomFieldsOptions" class="line-setting">
                <span class="title typographies-paragraph-medium-semibold">
                    {{ $l10n.__('Si une cellule de champ personnalisé de mon fichier est vide') }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    {{
                        getLabelSetting(
                            'use_default_values_for_empty_custom_fields',
                            importDetail._embed.metadata.use_default_values_for_empty_custom_fields,
                        )
                    }}
                </span>
            </p>
            <!--    Si une option de liste de mon fichier n'existe pas dans Sellsy        -->
            <p v-if="shouldDisplayCustomFieldsOptions" class="line-setting">
                <span class="title typographies-paragraph-medium-semibold">
                    {{ $l10n.__("Si une option de liste de mon fichier n'existe pas dans Sellsy") }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    {{
                        getLabelSetting(
                            'insert_new_custom_fields_enum_values',
                            importDetail._embed.metadata.insert_new_custom_fields_enum_values,
                        )
                    }}
                </span>
            </p>
        </div>
    </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const importsStore = createNamespacedHelpers('importsStore');
import Feature from 'Services/Feature';
export default {
    props: {
        isImportV2: {
            type: Boolean,
            required: true,
        },
    },
    computed: {
        ...importsStore.mapGetters({
            importDetail: 'getDetailImport',
            importOptions: 'getOptionsImport',
        }),
        shouldDisplayCustomFieldsOptions() {
            return (
                Feature.isEnabled('import-flatfile-custom-fields') &&
                ['product', 'company', 'contact', 'service', 'product', 'opportunity'].includes(
                    this.importDetail.type,
                )
            );
        },
        shouldDisplayCreateUpdateFieldsOptions() {
            return !(
                (Feature.isEnabled('import-flatfile-service-price') &&
                    this.importDetail?.type === 'service-price') ||
                (Feature.isEnabled('import-flatfile-product-price') &&
                    this.importDetail?.type === 'product-price')
            );
        },
    },
    methods: {
        getLabelIdentifier(key, value) {
            let label = '';
            if (value) {
                value.map((item, index) => {
                    if (index !== 0) {
                        label += ', ';
                    }
                    label +=
                        this.importOptions.identifiers[key]?.choices.find(
                            (choice) => choice.value === item,
                        )?.label ?? '';
                });
            }
            return label;
        },
        getLabelSetting(key, value) {
            let items = {};
            this.findItem(key, this.importOptions, (item) => (items = item));
            return (
                items?.values?.find((_value) => _value.value === (value ? 'Y' : 'N')).label ?? ''
            );
        },
        getCreateOrUpdateSettingLabel() {
            const { insert_new_records, update_existing_records } =
                this.importDetail._embed.metadata;
            if (!insert_new_records && !update_existing_records) {
                return '';
            }
            let value = 'create_and_update';
            if (!update_existing_records) {
                value = 'create_only';
            } else if (!insert_new_records) {
                value = 'update_only';
            }

            let items = {};
            this.findItem(
                'create_and_update_records',
                this.importOptions,
                (item) => (items = item),
            );

            return items?.values?.find((_value) => _value.value === value)?.label ?? '';
        },
        getTitleSetting(key) {
            let items = {};
            this.findItem(key, this.importOptions, (item) => (items = item));
            return items?.title ?? '';
        },
        findItem(_key, object, callback) {
            if (object && typeof object === 'object' && Object.values(object).length > 0) {
                for (const key of Object.keys(object)) {
                    if (key === _key) {
                        callback(object[key]);
                    } else {
                        this.findItem(_key, object[key], callback);
                    }
                }
            }
        },
    },
};
</script>
<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.list-settings-used {
    gap: var(--spacing-0);
    margin-top: var(--spacing-2);

    .line-setting {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-4);
        padding: var(--spacing-1);

        span {
            display: inline-block;
        }

        .title {
            width: 416px;
        }

        .value {
            margin-left: var(--spacing-4);
        }
    }

    .line-setting:nth-child(odd) {
        background-color: var(--colors-common-background-subtle);
        border-radius: var(--spacing-0);
    }
}
</style>
